// src/Pages/Clients/Sesiones/Sesiones.js
// Vista de sesiones del cliente.
//
// La tabla `fotos` es la fuente de verdad (igual que en el admin): lo que el
// admin ordena, describe o destaca se ve acá. Antes se leía el bucket directo
// y todo eso se perdía.
import React, { useState, useEffect, useCallback } from "react";
import { Camera, ImageOff, Clock, Star, Loader2 } from "lucide-react";
import { supabase } from "../../../Supabase/SupabaseClient";
import { listarFotos } from "../../../utils/fotosService";
import DrivePanel from "./DrivePanel";
import "./Sesiones.css";
import "./DrivePanel.css";
import Footer from "../../../Components/Footer/Footer";

const formatearFecha = (fechaStr) => {
  if (!fechaStr) return "Fecha no disponible";
  try {
    return new Date(fechaStr).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return fechaStr;
  }
};

const estadoBadge = (estado) => {
  if (estado === "completada") {
    return { clase: "sesiones-badge--completada", texto: "Completada" };
  }
  if (estado === "en_proceso" || estado === "en_roceso") {
    return { clase: "sesiones-badge--proceso", texto: "En proceso" };
  }
  return { clase: "sesiones-badge--pendiente", texto: "Pendiente" };
};

const Sesiones = () => {
  const [sesiones, setSesiones] = useState([]);
  const [sesionSeleccionada, setSesionSeleccionada] = useState(null);
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFotos, setLoadingFotos] = useState(false);
  const [error, setError] = useState(null);
  const [detallesError, setDetallesError] = useState(null);
  const [fotoAmpliada, setFotoAmpliada] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  const cargarFotos = useCallback(async (sesion) => {
    setLoadingFotos(true);
    try {
      const lista = await listarFotos(sesion.id);
      // Sin fallback al bucket a propósito: si la fila no está en la tabla, la
      // foto no existe para el cliente. Evita duplicados y fotos huérfanas.
      setFotos(lista);
    } catch (err) {
      console.error("Error al cargar las fotos:", err);
      setFotos([]);
      setDetallesError(err.message || String(err));
    } finally {
      setLoadingFotos(false);
    }
  }, []);

  const fetchSesionesUsuario = useCallback(
    async (userId) => {
      setLoading(true);
      setError(null);
      setDetallesError(null);

      try {
        // El filtro va en la consulta: sin sesión asignada, asignada a vos, o
        // ya completada. Antes se traían todas y se filtraban en memoria.
        const { data: sesionesList, error: errorSesiones } = await supabase
          .from("sesiones")
          .select("*")
          .or(
            `usuario_id.is.null,usuario_id.eq.${userId},estado.eq.completada`
          )
          .order("fecha", { ascending: false });

        if (errorSesiones) throw errorSesiones;

        const permitidas = (sesionesList || []).filter(
          (s) =>
            !s.usuario_id ||
            String(s.usuario_id).toLowerCase() === String(userId).toLowerCase() ||
            s.estado === "completada"
        );

        setSesiones(permitidas);

        if (permitidas.length === 0) {
          setSesionSeleccionada(null);
          setFotos([]);
          return;
        }

        const primera = permitidas[0];
        setSesionSeleccionada(primera);

        if (primera.estado === "completada") {
          await cargarFotos(primera);
        } else {
          setFotos([]);
        }
      } catch (err) {
        console.error("Error al cargar las sesiones:", err);
        setError("No se pudieron cargar tus sesiones fotográficas");
        setDetallesError(err.message || String(err));
      } finally {
        setLoading(false);
      }
    },
    [cargarFotos]
  );

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        setError("Debes iniciar sesión para ver tus sesiones");
        setLoading(false);
        return;
      }

      setCurrentUser(user);
      await fetchSesionesUsuario(user.id);
    };

    init();
  }, [fetchSesionesUsuario]);

  // Tras subir a Drive cambian `subido_google_drive` y las columnas `drive_*`.
  // Se relee solo esa sesion para no perder la seleccion ni recargar la foto
  // de la portada que el cliente ya esta mirando.
  const recargarSesionSeleccionada = useCallback(async () => {
    const id = sesionSeleccionada?.id;
    if (!id) return;

    const { data, error: err } = await supabase
      .from("sesiones")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (err || !data) return;

    setSesionSeleccionada((prev) => ({ ...prev, ...data }));
    setSesiones((lista) => lista.map((s) => (s.id === data.id ? { ...s, ...data } : s)));
  }, [sesionSeleccionada?.id]);

  const handleClickSesion = async (sesion) => {
    setError(null);
    setDetallesError(null);
    setSesionSeleccionada(sesion);

    if (sesion.estado === "completada") {
      await cargarFotos(sesion);
    } else {
      setFotos([]);
    }
  };

  const fotoDestacada = fotos.find((f) => f.destacada);

  return (
    <>
    <div className="sesiones-container">
      <h1 className="sesiones-title">Mis Sesiones</h1>

      {error ? (
        <div className="sesiones-card sesiones-card--narrow sesiones-error" role="alert">
          <h3 className="sesiones-error-title">{error}</h3>
          {detallesError && (
            <details className="sesiones-error-details">
              <summary>Ver detalles técnicos</summary>
              <pre>{detallesError}</pre>
            </details>
          )}
          {currentUser && (
            <button
              type="button"
              className="sesiones-btn"
              onClick={() => fetchSesionesUsuario(currentUser.id)}
            >
              Reintentar
            </button>
          )}
        </div>
      ) : loading ? (
        <div className="sesiones-card sesiones-card--narrow">
          <p className="sesiones-loading">Cargando tus sesiones...</p>
        </div>
      ) : sesiones.length === 0 ? (
        <div className="sesiones-card sesiones-card--narrow">
          <div className="sesiones-card-icon sesiones-empty-icon" aria-hidden="true">
            <Camera size={40} />
          </div>
          <h3>No tienes sesiones asignadas</h3>
          <p>Cuando tengas sesiones fotográficas asignadas, aparecerán aquí.</p>
        </div>
      ) : (
        <>
          <div
            className="sesiones-tabs"
            role="tablist"
            aria-label="Tus sesiones fotográficas"
          >
            {sesiones.map((sesion) => {
              const activa = sesionSeleccionada?.id === sesion.id;
              const badge = estadoBadge(sesion.estado);

              return (
                <button
                  type="button"
                  key={sesion.id}
                  role="tab"
                  aria-selected={activa}
                  className={`sesiones-tab${activa ? " sesiones-tab--active" : ""}`}
                  onClick={() => handleClickSesion(sesion)}
                >
                  <div>
                    {sesion.nombre || `Sesión ${formatearFecha(sesion.fecha)}`}
                  </div>
                  <div className="sesiones-tab-status">
                    <span className={`sesiones-badge ${badge.clase}`}>{badge.texto}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {sesionSeleccionada && (
            <div>
              <div className="sesiones-detail-header">
                <h2 className="sesiones-detail-title">
                  {sesionSeleccionada.nombre ||
                    `Sesión ${formatearFecha(sesionSeleccionada.fecha)}`}
                </h2>
                <p className="sesiones-detail-date">
                  {formatearFecha(sesionSeleccionada.fecha)}
                </p>
                {sesionSeleccionada.descripcion && (
                  <p className="sesiones-detail-desc">
                    {sesionSeleccionada.descripcion}
                  </p>
                )}
              </div>

              {sesionSeleccionada.estado !== "completada" ? (
                <div className="sesiones-card sesiones-card--narrow">
                  <div className="sesiones-card-icon sesiones-empty-icon" aria-hidden="true">
                    <Clock size={40} />
                  </div>
                  <h3>Sesión en proceso</h3>
                  <p>
                    Tu sesión fotográfica está siendo procesada. Una vez que esté completada,
                    podrás ver todas tus fotos aquí.
                  </p>
                </div>
              ) : (
                <>
                  <DrivePanel
                    sesion={sesionSeleccionada}
                    totalFotos={fotos.length}
                    alTerminar={recargarSesionSeleccionada}
                  />

                  {loadingFotos ? (
                    <div className="sesiones-card">
                      <p className="sesiones-loading">
                        <Loader2 size={16} className="sesiones-spin" /> Cargando fotos...
                      </p>
                    </div>
                  ) : fotos.length === 0 ? (
                    <div className="sesiones-card sesiones-card--narrow">
                      <div className="sesiones-card-icon sesiones-empty-icon" aria-hidden="true">
                        <ImageOff size={40} />
                      </div>
                      {sesionSeleccionada.subido_google_drive ? (
                        <p>
                          Las fotos de esta sesión ya se copiaron a tu Google Drive y
                          se retiraron de nuestro servidor.
                        </p>
                      ) : (
                        <p>No hay fotos disponibles en esta sesión</p>
                      )}
                    </div>
                  ) : (
                    <>
                      {fotoDestacada && (
                    <figure className="sesiones-hero">
                      <img
                        src={fotoDestacada.url_foto}
                        alt={
                          fotoDestacada.descripcion ||
                          `Foto destacada de ${sesionSeleccionada.nombre || "la sesión"}`
                        }
                      />
                      <figcaption className="sesiones-hero-caption">
                        <Star size={14} /> Destacada de la sesión
                        {fotoDestacada.descripcion ? ` — "${fotoDestacada.descripcion}"` : ""}
                      </figcaption>
                    </figure>
                  )}

                  <p className="sesiones-count">
                    {fotos.length} foto{fotos.length === 1 ? "" : "s"} disponible
                    {fotos.length === 1 ? "" : "s"}
                  </p>

                  <div className="sesiones-grid">
                    {fotos.map((foto) => (
                      <div
                        key={foto.id}
                        className="sesiones-photo"
                        role="button"
                        tabIndex={0}
                        onClick={() => setFotoAmpliada(foto)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setFotoAmpliada(foto);
                          }
                        }}
                      >
                        <img
                          src={foto.url_foto}
                          alt={foto.descripcion || "Foto de la sesión"}
                          loading="lazy"
                        />
                        {foto.descripcion && (
                          <span className="sesiones-photo-desc">{foto.descripcion}</span>
                        )}
                      </div>
                    ))}
                  </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}

      {fotoAmpliada && (
        <div
          className="sesiones-lightbox"
          onClick={() => setFotoAmpliada(null)}
          role="dialog"
          aria-modal="true"
        >
          <img src={fotoAmpliada.url_foto} alt={fotoAmpliada.descripcion || "Foto ampliada"} />
          {fotoAmpliada.descripcion && (
            <p className="sesiones-lightbox-caption">{fotoAmpliada.descripcion}</p>
          )}
        </div>
      )}
    </div>
      <Footer />
    </>
  );
};

export default Sesiones;